import React, { useEffect, useRef, useState } from 'react';
import { styled } from 'components/ThemeConfigConnector/ThemeConfigConnnector';
import video from 'assets/images/preloader-animation.webm';
import localConfig from 'config/localConfig';
import localTranslationsEn from 'config/translations-en.json';
import localTranslationsEt from 'config/translations-et.json';
import localTranslationsRu from 'config/translations-ru.json';
import { visuallyHidden } from 'style/styled-components/cssHeadings';
import translate from 'utils/fnTranslate';
import { getLocalStorage } from 'utils/fnStorage';
import { usePrefersReducedMotion } from 'hooks/usePrefersReducedMotion/usePrefersReducedMotion';

export const SplashScreenWrapper = styled.div`
    width: 100%;
    height: 100%;
    background-color: #000;

    video {
        display: block;
        width: 100%;
        height: 100%;
    }
`;

const VisuallyHidden = styled.div`
    ${visuallyHidden}
`;

const LOCAL_TRANSLATIONS = {
    en: localTranslationsEn.data,
    et: localTranslationsEt.data,
    ru: localTranslationsRu.data,
};

const LOADING_KEY = 'FTI_WELCOME_SCREEN_LOADING';
const STILL_FRAME_SECONDS = 6;

const getLoadingText = (): string => {
    const translation = translate(LOADING_KEY);

    if (translation !== LOADING_KEY) return translation;

    const language = getLocalStorage('appLanguage') || 'et';

    return (LOCAL_TRANSLATIONS[language] ?? LOCAL_TRANSLATIONS.et)[LOADING_KEY];
};

export const SplashScreen: React.FC = () => {
    const [status, setStatus] = useState('');
    const prefersReducedMotion = usePrefersReducedMotion();
    const videoRef = useRef<HTMLVideoElement>(null);

    const showStillFrame = () => {
        const videoElement = videoRef.current;

        if (!videoElement) return;

        videoElement.pause();
        videoElement.currentTime = Math.min(STILL_FRAME_SECONDS, videoElement.duration || STILL_FRAME_SECONDS);
    };

    useEffect(() => {
        if (prefersReducedMotion) {
            showStillFrame();
        } else {
            videoRef.current?.play().catch(() => {});
        }
    }, [prefersReducedMotion]);

    useEffect(() => {
        setStatus(`${localConfig.title}. ${getLoadingText()}`);
    }, []);

    return (
        <SplashScreenWrapper>
            <video
                ref={videoRef}
                loop
                autoPlay={!prefersReducedMotion}
                preload="auto"
                muted
                playsInline
                aria-hidden
                onLoadedMetadata={prefersReducedMotion ? showStillFrame : undefined}
            >
                <source src={video} type="video/webm" />
            </video>
            <VisuallyHidden role="status">{status}</VisuallyHidden>
        </SplashScreenWrapper>
    );
};
